"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { useRouter } from "@/i18n/navigation";
import { cn } from "@/lib/cn";
import { canGoBackInApp } from "@/lib/nav-back";
import { Wordmark } from "./Wordmark";

export interface TopBarProps {
  /** Right-aligned actions (buttons, avatar, etc.). */
  actions?: React.ReactNode;
  /**
   * Sub-screen mode: render a back arrow + this title instead of the brand
   * wordmark (native navigation pattern). Root tabs keep the wordmark.
   */
  title?: string;
  /** Where back lands when there is no history (cold start on a sub-screen). */
  backHref?: string;
  className?: string;
}

/**
 * Mobile/tablet top bar: brand wordmark on the left, actions on the right —
 * or, on sub-screens (`title` set), a back arrow + screen title. Transparent
 * at the top of the page, gaining a blurred translucent background +
 * hairline once the user scrolls. Hidden on desktop, where the left rail
 * owns the brand.
 */
export function TopBar({ actions, title, backHref = "/deck", className }: TopBarProps) {
  const t = useTranslations("common");
  const router = useRouter();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={cn(
        "sticky top-0 z-40 md:hidden",
        "pt-[env(safe-area-inset-top)] transition-colors duration-300",
        scrolled
          ? "border-b border-white/8 bg-ink/70 backdrop-blur-xl"
          : "border-b border-transparent",
        className,
      )}
    >
      <div className="flex h-14 items-center justify-between gap-3 px-4">
        {title ? (
          <div className="flex min-w-0 items-center gap-1">
            <button
              type="button"
              onClick={() => {
                // Cold start straight onto a sub-screen has no in-app history
                // to pop — fall back to the screen's natural parent.
                if (canGoBackInApp()) router.back();
                else router.replace(backHref);
              }}
              aria-label={t("back")}
              className="relative -ml-2 flex size-10 shrink-0 items-center justify-center rounded-full text-white/70 transition-colors after:absolute after:-inset-0.5 hover:bg-white/8 hover:text-white"
            >
              <svg viewBox="0 0 24 24" className="size-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <h1 className="truncate font-display text-lg font-semibold text-white">{title}</h1>
          </div>
        ) : (
          <Wordmark size="md" href="/deck" />
        )}
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
    </header>
  );
}
